import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ProductCard } from '../components/ProductCard';
import { productsAPI, categoriesAPI, analyticsAPI } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { useDebounce } from '../hooks/useDebounce';
import { Pagination } from '../components/Pagination';
import {
  Package, Search, X, SlidersHorizontal, LayoutGrid, LayoutList,
  ChevronDown, ChevronUp, Filter
} from 'lucide-react';

const ITEMS_PER_PAGE = 12;

/* ── Collapsible sidebar section ─────────────────────────── */
const SidebarSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-3.5 text-sm font-semibold text-slate-700 hover:text-[#8acb95] transition-colors"
      >
        {title}
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState(searchParams.get('search')   || '');
  const [category, setCategory]       = useState(searchParams.get('category') || 'all');
  const [brand, setBrand]             = useState(searchParams.get('brand')    || '');
  const [sortBy, setSortBy]           = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode]       = useState('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 400);

  /* Sync debounced search → URL */
  useEffect(() => {
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    const cat = category && category !== 'all' ? category : null;
    if (cat)   params.category = cat;
    if (brand) params.brand    = brand;
    setCurrentPage(1);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  /* Build API params */
  const queryParams = { is_active: true, page: currentPage, limit: ITEMS_PER_PAGE };
  const searchVal   = searchParams.get('search');
  const categoryVal = searchParams.get('category');
  const brandVal    = searchParams.get('brand');
  if (searchVal)   queryParams.search   = searchVal;
  if (categoryVal && categoryVal !== 'all') queryParams.category = categoryVal;
  if (brandVal)    queryParams.brand    = brandVal;

  /* Queries */
  const { data: categoriesRes } = useApiQuery(['categories'], () => categoriesAPI.getAll());

  const { data: productsData, isLoading: loading } = useApiQuery(
    ['products', queryParams, sortBy],
    async () => {
      const res = await productsAPI.getAll(queryParams);
      let sorted = [...(res.data.data || [])];
      if (sortBy === 'price_low')       sorted.sort((a, b) => a.price - b.price);
      else if (sortBy === 'price_high') sorted.sort((a, b) => b.price - a.price);
      else                              sorted.sort((a, b) => a.name.localeCompare(b.name));
      return { products: sorted, pagination: res.data.pagination };
    }
  );

  const categories = useMemo(() => categoriesRes?.data || [], [categoriesRes?.data]);
  const products   = productsData?.products  || [];
  const pagination = productsData?.pagination || { page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 };

  /* ── Derive available brands from categories data ─────── */
  const availableBrands = useMemo(() => {
    if (!categories.length) return [];
    const activeCat = searchParams.get('category');

    if (activeCat && activeCat !== 'all') {
      // Show brands for the selected category only
      const cat = categories.find((c) => c.name === activeCat);
      return (cat?.brands || []).slice().sort();
    }

    // All unique brands across all categories
    const allBrands = new Set();
    categories.forEach((cat) => (cat.brands || []).forEach((b) => allBrands.add(b)));
    return [...allBrands].sort();
  }, [categories, searchParams]);

  useEffect(() => {
    analyticsAPI.track({ event_type: 'page_view', page: 'products' }).catch(() => {});
  }, []);

  /* ── Handlers ─────────────────────────────────────────── */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applyParams = useCallback((newSearch, newCat, newBrand) => {
    const params = {};
    if (newSearch) params.search = newSearch;
    const cat = newCat && newCat !== 'all' ? newCat : null;
    if (cat)      params.category = cat;
    if (newBrand) params.brand    = newBrand;
    setCurrentPage(1);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const handleCategorySelect = (catName) => {
    const newCat = catName === category ? 'all' : catName;
    setCategory(newCat);
    // Reset brand when category changes — brands list will refresh
    setBrand('');
    applyParams(debouncedSearch, newCat, '');
  };

  const handleBrandSelect = (brandName) => {
    const newBrand = brandName === brand ? '' : brandName;
    setBrand(newBrand);
    applyParams(debouncedSearch, category, newBrand);
  };

  const clearFilters = () => {
    setSearchInput('');
    setBrand('');
    setCategory('all');
    setCurrentPage(1);
    setSearchParams({}, { replace: true });
  };

  const activeSearch   = searchParams.get('search')   || '';
  const activeCategory = searchParams.get('category') || '';
  const activeBrand    = searchParams.get('brand')    || '';
  const hasFilters     = activeSearch || (activeCategory && activeCategory !== 'all') || activeBrand;

  const startItem = pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem   = Math.min(pagination.page * pagination.limit, pagination.total);

  /* ── Shared sidebar content ───────────────────────────── */
  const SidebarContent = () => (
    <div className="space-y-0">

      {/* Search */}
      <SidebarSection title="Search" defaultOpen>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="search-input"
            className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8acb95] bg-white"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </SidebarSection>

      {/* Categories */}
      <SidebarSection title="Categories" defaultOpen>
        <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
          <button
            onClick={() => handleCategorySelect('all')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
              !activeCategory || activeCategory === 'all'
                ? 'bg-[#8acb95] text-white font-semibold'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
          >
            <span>All Categories</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleCategorySelect(cat.name)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                activeCategory === cat.name
                  ? 'bg-[#8acb95] text-white font-semibold'
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
            >
              <span className="truncate text-left">{cat.name}</span>
              <span className={`text-xs ml-2 flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                activeCategory === cat.name
                  ? 'bg-white/25 text-white'
                  : 'bg-slate-100 text-slate-500'
              }`}>
                {cat.count}
              </span>
            </button>
          ))}
        </div>
      </SidebarSection>

      {/* Brands — clickable list derived from categories */}
      <SidebarSection title={`Brand${availableBrands.length ? ` (${availableBrands.length})` : ''}`} defaultOpen={false}>
        {availableBrands.length === 0 ? (
          <p className="text-xs text-slate-400 px-1 py-2">
            {categories.length === 0 ? 'Loading...' : 'No brands available'}
          </p>
        ) : (
          <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
            {/* Clear brand selection */}
            {activeBrand && (
              <button
                onClick={() => handleBrandSelect(activeBrand)}
                className="w-full flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-500 transition-colors px-1 pb-2 border-b border-slate-100 mb-1"
              >
                <X className="w-3 h-3" /> Clear brand filter
              </button>
            )}
            {availableBrands.map((b) => (
              <button
                key={b}
                onClick={() => handleBrandSelect(b)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all text-left ${
                  activeBrand === b
                    ? 'bg-[#8acb95] text-white font-semibold'
                    : 'hover:bg-slate-50 text-slate-700'
                }`}
              >
                <span className="truncate">{b}</span>
                {activeBrand === b && (
                  <span className="text-white/70 text-xs ml-1 flex-shrink-0">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </SidebarSection>

      {/* Sort */}
      <SidebarSection title="Sort By" defaultOpen={false}>
        <div className="space-y-1">
          {[
            { value: 'name',       label: 'Name (A–Z)' },
            { value: 'price_low',  label: 'Price: Low to High' },
            { value: 'price_high', label: 'Price: High to Low' },
          ].map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="radio"
                name="sort"
                value={opt.value}
                checked={sortBy === opt.value}
                onChange={() => { setSortBy(opt.value); setCurrentPage(1); }}
                className="accent-[#8acb95]"
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>
      </SidebarSection>

      {/* Clear all */}
      {hasFilters && (
        <div className="pt-3">
          <button
            onClick={clearFilters}
            data-testid="clear-filters"
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-[#8acb95] text-[#8acb95] rounded-xl text-sm font-semibold hover:bg-[#8acb95] hover:text-white transition-all"
          >
            <X className="w-4 h-4" /> Clear All Filters
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      {/* Compact Hero */}
      <section className="hero-gradient py-8 border-b border-slate-200" data-testid="products-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-1"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {activeCategory && activeCategory !== 'all' ? activeCategory : 'All Products'}
          </h1>
          <p className="text-slate-500 text-sm">
            {pagination.total > 0
              ? `${pagination.total} products found`
              : 'Browse our complete collection'}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8 flex-1">
        <div className="flex gap-8">

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sticky top-28">
              <div className="flex items-center gap-2 mb-4">
                <SlidersHorizontal className="w-4 h-4 text-[#8acb95]" />
                <h2 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Filters</h2>
              </div>
              <SidebarContent />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">

            {/* Top bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Mobile filter button */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#8acb95] transition-colors shadow-sm"
                  data-testid="filters-section"
                >
                  <Filter className="w-4 h-4 text-[#8acb95]" />
                  Filters
                  {hasFilters && (
                    <span className="bg-[#8acb95] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {[activeSearch, activeCategory && activeCategory !== 'all', activeBrand].filter(Boolean).length}
                    </span>
                  )}
                </button>

                {/* Results count */}
                <p className="text-sm text-slate-500">
                  {pagination.total > 0 ? (
                    <span>
                      Showing <strong className="text-slate-800">{startItem}–{endItem}</strong> of{' '}
                      <strong className="text-slate-800">{pagination.total}</strong>
                    </span>
                  ) : (!loading && 'No products found')}
                </p>
              </div>

              {/* View toggle */}
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#8acb95] text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                  title="Grid view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#8acb95] text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                  title="List view"
                >
                  <LayoutList className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Active filter pills */}
            {hasFilters && (
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="text-xs text-slate-500 font-medium">Active:</span>
                {activeSearch && (
                  <button
                    onClick={() => setSearchInput('')}
                    className="flex items-center gap-1.5 text-xs bg-green-50 border border-green-200 text-[#6ab57e] font-medium px-3 py-1.5 rounded-full hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors"
                  >
                    Search: {activeSearch} <X className="w-3 h-3" />
                  </button>
                )}
                {activeCategory && activeCategory !== 'all' && (
                  <button
                    onClick={() => handleCategorySelect(activeCategory)}
                    className="flex items-center gap-1.5 text-xs bg-green-50 border border-green-200 text-[#6ab57e] font-medium px-3 py-1.5 rounded-full hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors"
                  >
                    {activeCategory} <X className="w-3 h-3" />
                  </button>
                )}
                {activeBrand && (
                  <button
                    onClick={() => handleBrandSelect(activeBrand)}
                    className="flex items-center gap-1.5 text-xs bg-green-50 border border-green-200 text-[#6ab57e] font-medium px-3 py-1.5 rounded-full hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors"
                  >
                    Brand: {activeBrand} <X className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={clearFilters}
                  data-testid="clear-filters"
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors underline underline-offset-2"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Products grid / list */}
            {loading ? (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5'
                : 'space-y-3'
              }>
                {[...Array(9)].map((_, i) => (
                  viewMode === 'grid' ? (
                    <div key={i} className="bg-white rounded-2xl overflow-hidden border border-slate-100">
                      <div className="aspect-[4/3] skeleton" />
                      <div className="p-4 space-y-2">
                        <div className="h-3 bg-slate-200 rounded skeleton w-1/3" />
                        <div className="h-4 bg-slate-200 rounded skeleton" />
                        <div className="h-4 bg-slate-200 rounded skeleton w-2/3" />
                      </div>
                    </div>
                  ) : (
                    <div key={i} className="bg-white rounded-xl border border-slate-100 h-36 skeleton" />
                  )
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100" data-testid="no-products">
                <Package className="w-14 h-14 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-700 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  No products found
                </h3>
                <p className="text-slate-400 mb-6 text-sm">Try adjusting or clearing your filters</p>
                <button
                  onClick={clearFilters}
                  data-testid="clear-filters-empty"
                  className="bg-[#8acb95] hover:bg-[#7bb885] text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5'
                : 'space-y-3'
              }>
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} view={viewMode} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && pagination.pages > 1 && (
              <div className="mt-10">
                <Pagination
                  currentPage={pagination.page}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-[#8acb95]" />
                <h2 className="font-bold text-slate-800">Filters</h2>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <SidebarContent />
            </div>
            <div className="p-5 border-t border-slate-100 sticky bottom-0 bg-white">
              <button
                onClick={() => setSidebarOpen(false)}
                className="w-full bg-[#8acb95] hover:bg-[#7bb885] text-white font-semibold py-3 rounded-xl text-sm transition-colors"
              >
                View {pagination.total > 0 ? pagination.total : ''} Results
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ProductsPage;
